import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Layers, Loader2, Pencil, Trash2, X, Search } from 'lucide-react';
import { settoriMerceologiciAPI } from '@offertify/api-client';
import type { SettoreMerceologico } from '@offertify/core';
import { DEFAULT_CANALE_COLOR, HEX_COLOR_REGEX, CANALE_PALETTE } from '../types/canale';
import { contrastColor } from '../utils/color';
import ConfirmModal from '../components/ConfirmModal';
import { usePermissions } from '../hooks/usePermissions';

interface EditForm {
  id?: string;
  nome: string;
  color: string;
  descrizione: string;
}

const emptyForm: EditForm = {
  nome: '',
  color: DEFAULT_CANALE_COLOR,
  descrizione: '',
};

export default function SettoriMerceologici() {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('settori:edit');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState<EditForm>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<SettoreMerceologico | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['settori-merceologici', { scope: 'list' }, { page, search }],
    queryFn: async () => {
      const res = await settoriMerceologiciAPI.getSettori({
        page,
        limit: 30,
        search: search || undefined,
      });
      return res.data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: (values: EditForm) => {
      const payload = {
        nome: values.nome,
        color: values.color,
        descrizione: values.descrizione || undefined,
      };
      if (values.id) {
        return settoriMerceologiciAPI.updateSettore(values.id, payload);
      }
      return settoriMerceologiciAPI.createSettore(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settori-merceologici'] });
      queryClient.invalidateQueries({ queryKey: ['settori-merceologici-select'] });
      toast.success(form.id ? 'Settore aggiornato' : 'Settore creato');
      setIsModalOpen(false);
      setForm(emptyForm);
    },
    onError: (error: any) => {
      const status = error.response?.status;
      if (status === 409) {
        toast.error('Nome settore già usato in questo tenant');
      } else {
        toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => settoriMerceologiciAPI.deleteSettore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settori-merceologici'] });
      queryClient.invalidateQueries({ queryKey: ['settori-merceologici-select'] });
      toast.success('Settore eliminato');
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    },
  });

  const settori = data?.data || [];
  const pagination = data?.pagination;

  const openCreate = () => {
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEdit = (c: SettoreMerceologico) => {
    setForm({
      id: c.id,
      nome: c.nome,
      color: c.color,
      descrizione: c.descrizione || '',
    });
    setIsModalOpen(true);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim()) {
      toast.error('Il nome è obbligatorio');
      return;
    }
    if (!HEX_COLOR_REGEX.test(form.color)) {
      toast.error('Colore non valido (formato #RRGGBB)');
      return;
    }
    saveMutation.mutate(form);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settori merceologici</h1>
          <p className="text-gray-600">
            Settori merceologici assegnabili alle promozioni (es. Elettrico, Idraulico)
          </p>
        </div>
        {canEdit && (
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-5 w-5" />
            Nuovo settore
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca settore..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : settori.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Layers className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nessun settore merceologico</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {settori.map((c) => {
              const fg = contrastColor(c.color);
              return (
                <div
                  key={c.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: c.color, color: fg }}
                    >
                      <Layers className="h-3 w-3" />
                      {c.nome}
                    </span>
                    {c.descrizione && (
                      <span className="text-sm text-gray-500 truncate">
                        {c.descrizione}
                      </span>
                    )}
                    <code className="text-xs text-gray-400">{c.color}</code>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit(c)}
                        className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                        title="Modifica"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(c)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        title="Elimina"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {pagination && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Pagina {pagination.page} di {pagination.pages} ({pagination.total} settori)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Successivo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal create/edit */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">
                {form.id ? 'Modifica settore' : 'Nuovo settore'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  required
                  maxLength={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Colore
                </label>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="h-10 w-14 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value={form.color}
                    onChange={(e) =>
                      setForm({ ...form, color: e.target.value.toLowerCase() })
                    }
                    pattern="^#[0-9a-fA-F]{6}$"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>
                <div className="flex flex-wrap gap-1">
                  {CANALE_PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, color: c })}
                      className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${
                        form.color === c ? 'border-gray-900' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                </div>
                {/* Preview */}
                <div className="mt-3">
                  <span className="text-xs text-gray-500">Anteprima:</span>
                  <span
                    className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: form.color,
                      color: contrastColor(form.color),
                    }}
                  >
                    <Layers className="h-3 w-3" />
                    {form.nome || 'esempio'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrizione
                </label>
                <textarea
                  value={form.descrizione}
                  onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {form.id ? 'Salva' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          isOpen={!!deleteTarget}
          title="Elimina settore merceologico"
          message={`Eliminare il settore "${deleteTarget.nome}"? Verrà rimosso da tutte le promozioni a cui è associato.`}
          confirmLabel="Elimina"
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onClose={() => setDeleteTarget(null)}
          isLoading={deleteMutation.isPending}
          variant="danger"
        />
      )}
    </div>
  );
}
